import { Children, type ReactElement, type ReactNode } from "react"
import styled from "styled-components"
import { type DataSource, useProvenance } from "./ProvenanceContext"

const Outline = styled.span<{ $source: DataSource; $inline: boolean }>`
  display: ${({ $inline }) => ($inline ? "inline-flex" : "block")};
  outline: 2px dashed
    ${({ theme, $source }) => ($source === "dce" ? theme.color.provenanceDce : theme.color.provenanceMlc)};
  outline-offset: 2px;
  border-radius: ${({ theme }) => theme.radius.sm};
`

/**
 * Whether `children` put anything on the page. `Children.toArray` drops exactly
 * the values React treats as nothing — `null`, `undefined`, booleans — which is
 * what a component returns to say "nothing worth showing".
 *
 * It cannot see through an element to the `null` that element's own render will
 * return, so a slot whose emptiness is decided inside a child must pass `null`
 * from the call site rather than the child. `LibraryCard` does.
 */
const rendersNothing = (children: ReactNode): boolean => Children.toArray(children).every(child => child === "")

interface ProvenanceProps {
  readonly source: DataSource
  readonly children: ReactNode
  /** For marking something inside a flow — a pill over artwork — rather than a whole card. */
  readonly inline?: boolean
}

/**
 * Marks its children with the system the data came from, but ONLY while the
 * overlay is on.
 *
 * Off, it renders `children` with no wrapper at all — not a hidden one. A demo
 * control must not change the layout, the DOM, or the accessibility tree of the
 * thing it is demonstrating, and an element that is always present is an element
 * that can always be got wrong.
 *
 * On, an empty slot outlines nothing rather than outlining emptiness: a labelled
 * box around no content claims `source` contributed something here when it did
 * not.
 */
export const Provenance = ({ source, children, inline = false }: ProvenanceProps): ReactElement => {
  const { isOn } = useProvenance()

  if (!isOn) {
    return <>{children}</>
  }

  if (rendersNothing(children)) {
    return <>{children}</>
  }

  return (
    <Outline $source={source} $inline={inline} data-qa-hook={`provenance-${source}`}>
      {children}
    </Outline>
  )
}
